"use client"

import ContactUsClient from "@/components/clients/ContactUsClient"
import SupportClient from "@/components/clients/SupportClient"
import SocialLinks from "@/components/layout/SocialLinks"
import { buttonVariants } from "@/components/ui/Button"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import Image from "next/image"
import React from "react"

/**
 * File: components/app-footer.tsx
 *
 * Footer component for the application.
 */

/**
 * AppFooter component provides footer information and links
 *
 * @component
 * @example
 * ```tsx
 * <AppFooter />
 * ```
 */
export default function AppFooter() {
  const [contactUsOpen, setContactUsOpen] = React.useState(false)
  const [supportOpen, setSupportOpen] = React.useState(false)
  const [user, setUser] = React.useState<unknown>(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    let cancelled = false
    const loadUser = async () => {
      try {
        const res = await fetch("/api/me", { cache: "no-store" })
        if (!cancelled && res.ok) {
          const data = await res.json()
          setUser(data?.user || null)
        }
      } catch {
        // User not logged in
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    loadUser()
    return () => {
      cancelled = true
    }
  }, [])

  const ORGANIZATION =
    process.env.NEXT_PUBLIC_ORGANIZATION || "International Union of Railways"

  return (
    <footer
      className="pt-6 p-2 text-sm border-t border-slate-500/10"
      role="contentinfo"
    >
      <div className="flex flex-col lg:flex-row items-center justify-center gap-10 pt-5 pb-10">
        <Image
          src="/uic_logo_quadri.png"
          alt="UIC Logo"
          width={250}
          height={100}
        />
        <div className="p-4 text-muted-foreground">
          <p className="text-lg font-bold">International union of railways</p>
          <p className="text-sm text-muted-foreground">
            Union Internationale des Chemins de fer (UIC)
          </p>
          <p>
            <strong className="opacity-70 text-xs">
              Headquarters address:
            </strong>
            <br /> 16 rue Jean Rey F-75015 Paris, France
          </p>
          <p>
            <strong className="opacity-70 text-xs">Tel:</strong> ( 33 1) 44 49
            20 20 <strong className="opacity-70 text-xs">Fax:</strong> ( 33 1)
            44 49 20 29
          </p>
          <p>
            <strong className="opacity-70 text-xs">E-mail:</strong>{" "}
            <a href="mailto:safety@uic.org">safety@uic.org</a>
          </p>
        </div>
        <Dialog open={contactUsOpen} onOpenChange={setContactUsOpen}>
          <DialogTrigger
            className={cn(
              buttonVariants({ variant: "ghost", size: "lg" }),
              "cursor-pointer p-20 bg-slate-500/5"
            )}
          >
            <h4 className="text-lg font-bold">Contact us</h4>
          </DialogTrigger>
          <DialogContent className="sm:max-w-3xl p-6">
            <DialogTitle className="sr-only">Contact us</DialogTitle>
            <ContactUsClient onClose={() => setContactUsOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2 border-t border-slate-500/10 py-4">
        <a
          className={buttonVariants({ variant: "link" })}
          href={
            process.env.NEXT_PUBLIC_PRIVACY_POLICY_URL ||
            "https://uic.org/privacy-policy"
          }
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Privacy Policy"
          title="Privacy Policy"
        >
          Privacy Policy
        </a>
        <div className="hidden">
          <Dialog open={contactUsOpen} onOpenChange={setContactUsOpen}>
            <DialogTrigger
              className={cn(
                buttonVariants({ variant: "link" }),
                "cursor-pointer"
              )}
            >
              Contact us
            </DialogTrigger>
            <DialogContent className="sm:max-w-3xl p-6">
              <DialogTitle className="sr-only">Contact us</DialogTitle>
              <ContactUsClient onClose={() => setContactUsOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
        {!loading && user ? (
          <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
            <DialogTrigger
              className={cn(
                buttonVariants({ variant: "link" }),
                "cursor-pointer"
              )}
            >
              Support
            </DialogTrigger>
            <DialogContent className="sm:max-w-3xl p-6">
              <DialogTitle className="sr-only">Support</DialogTitle>
              <SupportClient onClose={() => setSupportOpen(false)} />
            </DialogContent>
          </Dialog>
        ) : null}
        <SocialLinks />
      </div>
      <div className="my-1 text-xs font-extralight opacity-80 text-center">
        &copy; {ORGANIZATION}
      </div>
    </footer>
  )
}
